/**
 * CORE-AQ (Core-100 R4 X2) — an appointment SUGGESTED from a call, for the advisor to confirm or reject.
 *
 * "A conversation may propose an appointment; an advisor commits it." The proposal is a derived extraction
 * (`call_appointment_proposals`, written only by the extractor through `_shared/callTranscription.ts`); this panel
 * shows it AS a machine suggestion and lets a front-office person decide it through the one server authority,
 * `decide_call_appointment_proposal` — confirm against the ticket being viewed (the appointment the advisor committed),
 * or reject with a reason. Nothing is booked or linked by the proposal itself. Only the front office can read
 * proposals (RLS), so for anyone else this renders nothing.
 *
 * Core-100 R5: an undecided, CURRENT proposal can also become the appointment ticket — "Create ticket from this call"
 * opens Create Ticket prefilled from it (src/lib/callProposal/callProposalTicket.ts); the advisor reviews and submits
 * through the normal path, and the database confirms the proposal against the new ticket in the same transaction.
 */
import { useCallback, useEffect, useState } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { createTicketFromCallProposalPath } from '@/lib/callProposal/callProposalTicket';

/** Opens Create Ticket prefilled from the proposal (navigation only; nothing is written here). */
function CreateTicketFromCallButton({ proposalId, disabled }: { proposalId: string; disabled: boolean }) {
  const navigate = useNavigate();
  return (
    <Button size="sm" variant="outline" disabled={disabled} data-testid="call-proposal-create-ticket"
      onClick={() => navigate(createTicketFromCallProposalPath(proposalId))}>
      Create ticket from this call
    </Button>
  );
}

/** Same words as DERIVED_PROPOSAL_LABEL in supabase/functions/_shared/callTranscription.ts (pinned by a test). */
export const CALL_PROPOSAL_LABEL = 'Suggested from the call by machine — not booked until an advisor confirms it';

export interface CallAppointmentProposal {
  id: string;
  service_summary: string;
  proposed_date: string | null;
  proposed_window: string | null;
  vehicle_hint: string | null;
  confidence: number | null;
  status: 'proposed' | 'confirmed' | 'rejected';
  created_at: string;
}

interface Props {
  callEventId: string;
  /** The ticket being viewed: the appointment a confirmation commits the proposal to. */
  ticketId: string | null;
}

export function CallAppointmentProposals({ callEventId, ticketId }: Props) {
  const [proposal, setProposal] = useState<CallAppointmentProposal | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const inRouter = useInRouterContext();

  const load = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('call_appointment_proposals' as never)
        .select('id, service_summary, proposed_date, proposed_window, vehicle_hint, confidence, status, created_at')
        .eq('call_event_id', callEventId)
        .order('created_at', { ascending: false })
        .limit(1);
      // Only the LATEST extraction may be decided (an older one is stale on the server too).
      setProposal(error ? null : (((data ?? []) as CallAppointmentProposal[])[0] ?? null));
    } catch {
      setProposal(null); // nothing is shown when it cannot be read — never a guess
    }
  }, [callEventId]);

  useEffect(() => { void load(); }, [load]);

  const decide = async (decision: 'confirm' | 'reject') => {
    if (!proposal) return;
    setBusy(true);
    setMessage(null);
    const { error } = await supabase.rpc('decide_call_appointment_proposal' as never, {
      p_proposal_id: proposal.id,
      p_decision: decision,
      p_ticket_id: decision === 'confirm' ? ticketId : null,
      p_note: decision === 'reject' ? reason.trim() : null,
    } as never);
    setBusy(false);
    if (error) {
      setMessage(
        /CALL_PROPOSAL_STALE/.test(error.message) ? 'A newer suggestion replaced this one.'
          : /CALL_PROPOSAL_ALREADY_DECIDED/.test(error.message) ? 'Someone already decided this suggestion.'
            : /CALL_PROPOSAL_FORBIDDEN/.test(error.message) ? 'Only the front office can decide this suggestion.'
              : 'The suggestion could not be decided.',
      );
      await load();
      return;
    }
    setRejecting(false);
    await load();
  };

  if (!proposal) return null;

  return (
    <div className="mt-2 rounded-md border border-dashed p-2 text-xs" data-testid="call-appointment-proposal">
      <p className="font-medium text-muted-foreground" data-testid="call-proposal-label">{CALL_PROPOSAL_LABEL}</p>
      <p className="mt-1">{proposal.service_summary}</p>
      <p className="text-muted-foreground">
        {[proposal.proposed_date, proposal.proposed_window, proposal.vehicle_hint].filter(Boolean).join(' · ') || 'No day or time was heard'}
      </p>
      {proposal.status === 'proposed' ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={busy || !ticketId}
            onClick={() => void decide('confirm')}
            title={ticketId ? undefined : 'Open the appointment ticket to confirm against it'}
          >
            Confirm on this ticket
          </Button>
          {inRouter && <CreateTicketFromCallButton proposalId={proposal.id} disabled={busy} />}
          {!rejecting ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setRejecting(true)}>Reject</Button>
          ) : (
            <>
              <Textarea
                aria-label="Reason for rejecting"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="h-12 text-xs"
              />
              <Button size="sm" variant="outline" disabled={busy || !reason.trim()} onClick={() => void decide('reject')}>
                Reject suggestion
              </Button>
            </>
          )}
        </div>
      ) : (
        <p className="mt-1 font-medium" data-testid="call-proposal-decided">
          {proposal.status === 'confirmed' ? 'Confirmed by the advisor' : 'Rejected by the advisor'}
        </p>
      )}
      {message && <p className="mt-1 text-red-700" role="alert">{message}</p>}
    </div>
  );
}
